import React, { useState, useEffect } from "react";
import { Row, Col, Form, Input, Button, Checkbox } from "antd";
import { Link, useLocation } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import jwt_decode from "jwt-decode";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import logoText from "@assets/images/logo/dark-logo-text-nobg.png";
import LeftContent from "./leftContent";
import { loginAction, googleLoginAction } from "@redux/actions/authActions";
import { googleCreateOrLogin } from "@api/auth/auth";
import { ErrorNotification } from "@components";
import "@assets/css/auth.css";

const Login = ({ history }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  // Check if opened from extension
  const searchParams = new URLSearchParams(location.search);
  const isFromExtension = searchParams.get("source") === "extension";
  const isOAuthFlow = searchParams.get("oauth") === "google";

  // Show extension-specific UI message
  useEffect(() => {
    if (isFromExtension) {
      // If extension opened for Google OAuth, we show a simplified view
      console.log("Login opened from Copilotly extension");
    }
  }, [isFromExtension]);

  // Handle email password login
  const handleLoginSubmit = (e) => {
    // Validate email regex
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!emailRegex.test(email)) {
      ErrorNotification("Please enter a valid email");
      return;
    }

    // Make sure the password is at least 6 characters
    if (password.length < 6) {
      ErrorNotification("Please enter a valid password");
      return;
    }

    e.preventDefault();
    // Dispatch email, password and history to action
    dispatch(loginAction({ email, password, history, setLoading }));
  };

  // Handle google login success
  const handleGoogleSuccess = async (credentialResponse) => {
    const userObject = jwt_decode(credentialResponse.credential);
    const { name, email } = userObject;
    setLoading(true);

    // If opened from extension, handle differently
    if (isFromExtension && window.opener) {
      try {
        // Call the API directly to get the token
        const res = await googleCreateOrLogin(name, email);

        if (res.data.accessToken) {
          // Redirect to success page which will handle postMessage and close
          const userData = encodeURIComponent(JSON.stringify(res.data.user));
          window.location.href = `/extension/auth/success?token=${res.data.accessToken}&user=${userData}`;
        }
      } catch (error) {
        // Send error message to extension
        window.opener.postMessage({
          type: "GOOGLE_AUTH_ERROR",
          error: error.response?.data?.message || "Google authentication failed"
        }, "*");
        setLoading(false);
      }
      return;
    }

    // Normal flow - dispatch to Redux
    dispatch(googleLoginAction({ name, email, history, setLoading }));
  };

  // Handle google login failure
  const handleGoogleFailure = () => {
    console.log("GOOGLE_LOGIN_ERROR");

    // If opened from extension, notify the extension
    if (isFromExtension && window.opener) {
      window.opener.postMessage({
        type: "GOOGLE_AUTH_ERROR",
        error: "Google login failed"
      }, "*");
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | Copilotly - Your Personal AI Copilot</title>
        <meta name="description" content="Log in to your Copilotly account. Access your AI-powered copilots for Legal, Medical, Financial, Marketing, Code, and Writing assistance." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://app.copilotly.com/login" />
        <meta property="og:title" content="Login | Copilotly - Your Personal AI Copilot" />
        <meta property="og:description" content="Log in to your Copilotly account. Access your AI-powered copilots." />
        <meta property="og:url" content="https://app.copilotly.com/login" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Row
        gutter={[32, 0]}
        className="hp-authentication-page"
        style={{ overflowY: "auto", height: "100vh" }}
      >
        <LeftContent />

        <Col
          lg={8}
          md={16}
          sm={16}
          span={24}
          className="hp-py-sm-0 hp-py-md-0"
          justify="center"
        >
          <Row className="hp-h-100" align="middle" justify="center">
            <Col
              xxl={11}
              xl={15}
              lg={20}
              md={16}
              sm={24}
              className="hp-px-sm-8"
              style={{ paddingTop: "12px", paddingBottom: "16px" }}
            >
              <img
                src={logoText}
                alt="logo"
                style={{
                  maxWidth: "140px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto 8px",
                }}
              />
              <h2
                className="hp-mb-sm-0"
                style={{
                  textAlign: "center",
                  fontSize: "18px",
                  lineHeight: "24px",
                  color: "rgba(0, 0, 0, 0.85)",
                  marginBottom: "12px",
                }}
              >
                Login
              </h2>

              <div
                className="google-login-button"
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <GoogleLogin
                  onSuccess={(credentialResponse) => {
                    handleGoogleSuccess(credentialResponse);
                  }}
                  onError={() => {
                    handleGoogleFailure();
                  }}
                />
              </div>
              <div className="divider" style={{ margin: "8px 0", fontSize: "12px" }}>Or</div>

              <Form
                layout="vertical"
                name="basic"
                initialValues={{ remember: true }}
                style={{ fontSize: "13px" }}
              >
                <Form.Item
                  style={{ marginBottom: "10px" }}
                  rules={[
                    { required: true, message: "Please enter your email" },
                  ]}
                >
                  <Input
                    id="validating"
                    value={email}
                    placeholder="Email"
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ height: "36px", fontSize: "13px" }}
                  />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "8px" }}
                  rules={[
                    { required: true, message: "Please enter your password" },
                  ]}
                >
                  <Input.Password
                    id="warning2"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ height: "36px", fontSize: "13px" }}
                  />
                </Form.Item>

                <div className="auth-forgot-box" style={{ marginBottom: "10px", fontSize: "12px" }}>
                  <Form.Item className="hp-mb-0">
                    <Checkbox name="remember" style={{ fontSize: "12px" }}>Remember me</Checkbox>
                  </Form.Item>

                  <Link to="/forgot-password" style={{ fontSize: "12px" }}>Forgot Password?</Link>
                </div>

                <Form.Item style={{ marginBottom: "8px" }}>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    onClick={handleLoginSubmit}
                    loading={loading}
                    style={{ height: "36px", fontSize: "13px" }}
                  >
                    Sign in
                  </Button>
                </Form.Item>
              </Form>

              <div className="login-create-acc" style={{ marginBottom: "6px", fontSize: "13px" }}>
                <p style={{ margin: 0 }}>Don't have an account?</p>
                <Link to="/signup" style={{ marginLeft: "4px" }}> Sign up</Link>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Col
                  className="hp-other-links"
                  style={{
                    justifyContent: "center",
                    display: "flex",
                    marginTop: "12px",
                  }}
                >
                  <Link
                    to="/privacy"
                    className="hp-text-color-black-80 hp-text-color-dark-40"
                    style={{
                      marginRight: "8px",
                      fontSize: "11px",
                    }}
                  >
                    Privacy Policy
                  </Link>

                  <span
                    className="hp-text-color-black-80 hp-text-color-dark-40"
                    style={{ margin: "0 6px", fontSize: "11px" }}
                  >
                    ·
                  </span>

                  <Link
                    to="/terms"
                    className="hp-text-color-black-80 hp-text-color-dark-40"
                    style={{ fontSize: "11px" }}
                  >
                    Terms of Service
                  </Link>
                </Col>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Login;
